import React, { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Lightbulb, Users, BookOpen, GraduationCap,
  ClipboardList, UserCircle, ShieldCheck, LogOut, ArrowLeft,
  Inbox, Briefcase, FileText, BarChart3, Settings, Building2,
  HelpCircle, CreditCard, Tags, Video, FileCheck2, UploadCloud, UserCheck, Wallet, Calendar, Clock, Receipt, TrendingUp,
  FolderTree, Database, Headphones, Sparkles, Mail, ChevronDown, Plus, Image as ImageIcon, Map, Menu, X, Megaphone, Bell, ChevronLeft, ChevronRight, MessageCircle
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import { UpcomingEventsBanner } from "@/components/EventsWidgets";
import { BRAND_ASSETS } from "@/lib/brandAssets";

const CLIENT_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/dashboard/employee/kanban", label: "Task Board", icon: Briefcase },
  { to: "/dashboard/employee/assigned-projects", label: "Assigned Projects", icon: FolderTree },
  { to: "/dashboard/ideas", label: "Ongoing Research", icon: Lightbulb },
  { to: "/dashboard/guidance", label: "Custom Guidance", icon: HelpCircle },
  { to: "/dashboard/mentors", label: "Mentors", icon: Users },
  { to: "/dashboard/journals", label: "Journals & Conferences", icon: BookOpen },
  { to: "/dashboard/courses", label: "Learn (LMS)", icon: GraduationCap },
  { to: "/dashboard/surveys", label: "Surveys", icon: ClipboardList },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: UserCircle },
  { to: "/dashboard/plagiarism", label: "Plagiarism Check", icon: ShieldCheck },
];

const STUDENT_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/dashboard/student-upload", label: "Submit Project / Thesis", icon: UploadCloud },
  { to: "/dashboard/ideas", label: "My Ideas", icon: Lightbulb },
  { to: "/dashboard/guidance", label: "Custom Guidance", icon: HelpCircle },
  { to: "/dashboard/mentors", label: "Mentors", icon: Users },
  { to: "/dashboard/journals", label: "Journals", icon: BookOpen },
  { to: "/dashboard/courses", label: "Learn (LMS)", icon: GraduationCap },
  { to: "/dashboard/surveys", label: "Surveys", icon: ClipboardList },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: UserCircle },
];

// Intern = student + client hybrid + daily-log + clock-in tracking.
const INTERN_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/dashboard/employee/kanban", label: "Task Board", icon: Briefcase },
  { to: "/dashboard/assign-tasks", label: "Assign Tasks", icon: ClipboardList },
  { to: "/dashboard/employee/assigned-projects", label: "Task Workspace", icon: FolderTree, activePrefixes: ["/dashboard/tasks/"] },
  { to: "/dashboard/employee/teams", label: "My Teams", icon: Users },
  { to: "/dashboard/employee/submissions", label: "Work Submissions", icon: UploadCloud },
  { to: "/dashboard/ideas", label: "My Ideas", icon: Lightbulb },
  { to: "/dashboard/guidance", label: "Custom Guidance", icon: HelpCircle },
  { to: "/dashboard/intern/daily-log", label: "Daily Log", icon: ClipboardList },
  { to: "/dashboard/employee/meetings", label: "Meetings", icon: Video },
  {
    label: "ERP", icon: Wallet, group: true,
    items: [
      { to: "/dashboard/employee/attendance", label: "Attendance", icon: Clock },
      { to: "/dashboard/employee/payslips", label: "Payslips", icon: CreditCard },
      { to: "/dashboard/employee/erp/leaves", label: "Leaves", icon: Calendar },
      { to: "/dashboard/employee/erp/documents", label: "Documents", icon: FileText },
      { to: "/dashboard/employee/erp/expenses", label: "Expenses", icon: Receipt },
      { to: "/dashboard/employee/erp/training", label: "Training", icon: GraduationCap },
    ],
  },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: UserCircle },
];

// Employee = full staff dashboard. Wraps intern role + adds payroll + ERP.
const EMPLOYEE_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/dashboard/employee/kanban", label: "Task Board", icon: Briefcase },
  { to: "/dashboard/assign-tasks", label: "Assign Tasks", icon: ClipboardList, hideForRoles: ["intern"] },
  { to: "/dashboard/employee/assigned-projects", label: "Task Workspace", icon: FolderTree, activePrefixes: ["/dashboard/tasks/"] },
  { to: "/dashboard/employee/teams", label: "My Teams", icon: Users },
  { to: "/admin/teams", label: "Project Teams", icon: Users, hideForRoles: ["intern"] },
  { to: "/dashboard/employee/direct-reports", label: "Direct Reports", icon: Users, hideForRoles: ["intern"] },
  { to: "/dashboard/employee/submissions", label: "Work Submissions", icon: UploadCloud },
  { to: "/dashboard/ideas", label: "My Ideas", icon: Lightbulb },
  { to: "/dashboard/guidance", label: "Custom Guidance", icon: HelpCircle },
  { to: "/dashboard/intern/daily-log", label: "Daily Log", icon: ClipboardList },
  { to: "/dashboard/employee/meetings", label: "Meetings", icon: Video },
  {
    label: "ERP", icon: Wallet, group: true,
    items: [
      { to: "/dashboard/employee/attendance", label: "Attendance", icon: Clock },
      { to: "/dashboard/employee/payslips", label: "Payslips", icon: CreditCard },
      { to: "/dashboard/employee/erp/leaves", label: "Leaves", icon: Calendar },
      { to: "/dashboard/employee/erp/documents", label: "Documents", icon: FileText },
      { to: "/dashboard/employee/erp/expenses", label: "Expenses", icon: Receipt },
      { to: "/dashboard/employee/erp/performance", label: "Performance", icon: TrendingUp },
      { to: "/dashboard/employee/erp/training", label: "Training", icon: GraduationCap },
    ],
  },
  { to: "/dashboard/portfolio", label: "Portfolio", icon: UserCircle },
];

const MENTOR_NAV = [
  { to: "/mentor", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/dashboard/employee/kanban", label: "Task Board", icon: Briefcase },
  { to: "/dashboard/employee/assigned-projects", label: "Assigned Projects", icon: FolderTree },
  { to: "/mentor/requests", label: "Incoming Requests", icon: Inbox },
  { to: "/mentor/projects", label: "Mentor Projects", icon: Briefcase },
  { to: "/dashboard/guidance", label: "Custom Guidance", icon: HelpCircle },
  { to: "/dashboard/ideas", label: "My Ideas", icon: Lightbulb },
  { to: "/mentor/earnings", label: "Course Earnings", icon: Wallet },
  { to: "/dashboard/courses?tab=mine", label: "My Courses", icon: BookOpen },
  { to: "/dashboard/journals", label: "Journals & Conferences", icon: BookOpen },
  { to: "/dashboard/portfolio", label: "My Portfolio", icon: UserCircle },
];

const INSTITUTION_NAV = [
  { to: "/institution", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
  { to: "/institution/members", label: "Members", icon: Users },
  { to: "/institution/projects", label: "Projects", icon: Lightbulb },
  { to: "/dashboard/journals", label: "Journals", icon: BookOpen },
  { to: "/dashboard/courses", label: "LMS", icon: GraduationCap },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users & Roles", icon: Users },
  { to: "/admin/role-approvals", label: "Role Approvals", icon: UserCheck },
  { to: "/admin/pending-ideas", label: "Pending Ideas", icon: FileCheck2 },
  { to: "/admin/mentor-requests", label: "Mentor Requests", icon: Inbox },
  { to: "/admin/guidance", label: "Guidance Queue", icon: HelpCircle },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/pricing", label: "Pricing & Coupons", icon: Tags },
  { to: "/admin/plans", label: "Subscription Plans", icon: Sparkles },
  { to: "/admin/email-engine", label: "Analytics & Emails", icon: Mail },
  { to: "/admin/mailbox", label: "Mailbox", icon: Inbox },
  { to: "/admin/course-review", label: "Course Review", icon: FileCheck2 },
  { to: "/admin/research-ideas", label: "Research Ideas", icon: Lightbulb },
  { to: "/admin/datasets", label: "Datasets", icon: Database },
  { to: "/admin/explainers", label: "Paper Explainers", icon: Headphones },
  { to: "/admin/taxonomy", label: "Taxonomy", icon: FolderTree },
  { to: "/admin/journals", label: "Journals", icon: BookOpen },
  { to: "/admin/blogs", label: "Blog / Journal Posts", icon: FileText },
  { to: "/admin/landing-journey", label: "Landing Roadmap", icon: Map },
  { to: "/admin/tutorials", label: "Tutorials", icon: Video },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/papers", label: "Published Papers", icon: FileText },
  { to: "/admin/projects", label: "All Projects", icon: Briefcase },
  { to: "/admin/interns", label: "Employee Analytics", icon: ClipboardList },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/config", label: "Platform Config", icon: Settings },
];

// Grouped admin nav — used by desktop sidebar for better organization.
// Each section is collapsible; tabs are grouped by feature domain.
const ADMIN_NAV_GROUPS = [
  {
    key: "overview",
    label: null, // pinned, no header
    items: [{ to: "/admin", label: "Overview", icon: LayoutDashboard, end: true }],
  },
  {
    key: "communications",
    label: "Communications",
    icon: Mail,
    items: [
      { to: "/admin/mailbox", label: "Mailbox", icon: Inbox },
      { to: "/dashboard/announcements", label: "Announcements", icon: Megaphone },
  { to: "/dashboard/chat", label: "Chat", icon: MessageCircle },
      { to: "/admin/email-engine", label: "Analytics & Campaigns", icon: BarChart3 },
      { to: "/admin/leads", label: "Client Leads", icon: FileText },
    ],
  },
  {
    key: "users",
    label: "Users & Access",
    icon: Users,
    items: [
      { to: "/admin/users", label: "Users & Roles", icon: Users },
      { to: "/admin/role-approvals", label: "Role Approvals", icon: UserCheck },
      { to: "/admin/mentor-requests", label: "Mentor Requests", icon: Inbox },
      { to: "/admin/vacancies", label: "Careers / Vacancies", icon: Briefcase },
      { to: "/admin/teams", label: "Project Teams", icon: Users },
      { to: "/admin/org", label: "Org Units", icon: Users },
      { to: "/dashboard/assign-tasks", label: "Assign Tasks", icon: ClipboardList },
      { to: "/admin/interns", label: "Employee Analytics", icon: ClipboardList },
    ],
  },
  {
    key: "ideas",
    label: "Ideas & Workflow",
    icon: Lightbulb,
    items: [
      { to: "/admin/pending-ideas", label: "Pending Ideas", icon: FileCheck2 },
      { to: "/admin/projects", label: "All Projects", icon: Briefcase },
      { to: "/admin/papers", label: "Published Papers", icon: FileText },
    ],
  },
  {
    key: "guidance",
    label: "Guidance",
    icon: HelpCircle,
    items: [
      { to: "/admin/guidance", label: "Guidance Queue", icon: HelpCircle },
    ],
  },
  {
    key: "lms",
    label: "LMS — Learning",
    icon: GraduationCap,
    items: [
      { to: "/admin/course-review", label: "Course Review", icon: FileCheck2 },
      { to: "/dashboard/courses", label: "All Courses", icon: BookOpen },
      { to: "/admin/tutorials", label: "Tutorials", icon: Video },
    ],
  },
  {
    key: "content",
    label: "Premium Content",
    icon: Sparkles,
    items: [
      { to: "/admin/research-ideas", label: "Research Ideas", icon: Lightbulb },
      { to: "/admin/datasets", label: "Datasets", icon: Database },
      { to: "/admin/explainers", label: "Paper Explainers", icon: Headphones },
      { to: "/admin/journals", label: "Journals & Conferences", icon: BookOpen },
      { to: "/admin/blogs", label: "Blog / Journal Posts", icon: FileText },
      { to: "/admin/landing-journey", label: "Landing Roadmap", icon: Map },
      { to: "/admin/taxonomy", label: "Taxonomy", icon: FolderTree },
    ],
  },
  {
    key: "events",
    label: "Events & Webinars",
    icon: Calendar,
    items: [
      { to: "/admin/events", label: "All Events", icon: Calendar },
    ],
  },
  {
    key: "monetization",
    label: "Monetization",
    icon: CreditCard,
    items: [
      { to: "/admin/payments", label: "Payments", icon: CreditCard },
      { to: "/admin/pricing", label: "Pricing & Coupons", icon: Tags },
      { to: "/admin/plans", label: "Subscription Plans", icon: Sparkles },
    ],
  },
  {
    key: "system",
    label: "Insights & System",
    icon: Settings,
    items: [
      { to: "/admin/analytics", label: "Site Analytics", icon: BarChart3 },
      { to: "/admin/media", label: "Media Manager", icon: ImageIcon },
      { to: "/admin/cms", label: "Content Library (CMS)", icon: FolderTree },
      { to: "/admin/nav-visibility", label: "Sidebar Visibility", icon: Settings },
      { to: "/admin/config", label: "Platform Config", icon: Settings },
    ],
  },
];

function getNavForRole(role) {
  switch (role) {
    case "admin":
    case "super_admin": return ADMIN_NAV;
    case "mentor":
    case "tutor": return MENTOR_NAV;
    case "partner_institution": return INSTITUTION_NAV;
    case "employee": return EMPLOYEE_NAV;
    case "intern": return INTERN_NAV;
    case "student": return STUDENT_NAV;
    default: return CLIENT_NAV;
  }
}

/**
 * Merge nav lists across every role the user holds (primary + secondary),
 * de-duplicating by target URL (`to`) and group label. This is what makes
 * a user assigned "employee + mentor" see BOTH the employee task board AND
 * the mentor console entries without either menu disappearing.
 *
 * Feature-flag support: any nav item (or grouped sub-item) may declare
 *   • `hideForRoles: ["role1", "role2"]` — hidden when the user holds
 *     ANY of those roles (primary or secondary).
 *   • `showForRoles: ["role1"]`         — shown only when the user holds
 *     ANY of those roles. Omit to allow everyone.
 * Both flags apply to individual items and to entire groups.
 *
 * Admin/super_admin is intentionally NOT merged here — the render path uses
 * ADMIN_NAV_GROUPS directly for those roles.
 */
function getNavForRoles(primary, secondary = [], isIntern = false) {
  const roles = new Set([primary, ...(secondary || [])].filter(Boolean));
  if (isIntern) roles.add("intern");
  // Intern-mode override: if the caller is flagged as intern (either primary
  // or `is_intern=true`), drop the fuller employee/mentor/tutor nav from the
  // merge — interns must never see Assign Tasks / Direct Reports / Project
  // Teams / Course Earnings etc. Secondary roles like admin still merge.
  const internMode = roles.has("intern");
  const passesFlags = (it) => {
    if (Array.isArray(it.hideForRoles) && it.hideForRoles.some((r) => roles.has(r))) return false;
    if (Array.isArray(it.showForRoles) && it.showForRoles.length && !it.showForRoles.some((r) => roles.has(r))) return false;
    return true;
  };
  const merged = [];
  const seenKey = new Set();       // by URL / group id
  const seenLabel = new Set();     // by visible label — kills the "Overview" duplicate
  const push = (item) => {
    if (!passesFlags(item)) return;
    const key = item.to || `group:${item.label}`;
    const labelKey = (item.label || "").toLowerCase().trim();
    if (seenKey.has(key)) return;
    if (!item.group && labelKey && seenLabel.has(labelKey)) return;
    if (item.group && item.items) {
      const kept = item.items.filter(passesFlags);
      if (kept.length === 0) return;
      const existing = merged.find((m) => m.group && m.label === item.label);
      if (existing) {
        const subSeen = new Set(existing.items.map((i) => i.to));
        const subLabels = new Set(existing.items.map((i) => (i.label || "").toLowerCase().trim()));
        for (const sub of kept) {
          const sl = (sub.label || "").toLowerCase().trim();
          if (subSeen.has(sub.to) || (sl && subLabels.has(sl))) continue;
          existing.items.push(sub); subSeen.add(sub.to); subLabels.add(sl);
        }
        return;
      }
      merged.push({ ...item, items: [...kept] });
    } else {
      merged.push(item);
    }
    seenKey.add(key);
    if (labelKey) seenLabel.add(labelKey);
  };
  // Iterate roles in a deterministic order so higher-privilege roles win
  // first. Intern-mode skips employee/mentor/tutor role navs entirely.
  const orderedRoles = [];
  for (const r of ["admin", "super_admin", "employee", "intern", "mentor", "tutor", "partner_institution", "student", "client"]) {
    if (!roles.has(r)) continue;
    if (internMode && ["employee", "mentor", "tutor"].includes(r)) continue;
    orderedRoles.push(r);
  }
  for (const r of orderedRoles) {
    for (const it of getNavForRole(r)) push(it);
  }
  return merged;
}

export default function DashboardLayout() {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin"
    || (user?.secondary_roles || []).some((r) => r === "admin" || r === "super_admin");

  // Super-Admin-configurable per-role hidden nav keys. Applied on top of the
  // static role → nav map, so admins can hide items without a code change.
  const [navVisibility, setNavVisibility] = React.useState({});
  const [navOrder, setNavOrder] = React.useState({});
  React.useEffect(() => {
    if (!user) return;
    import("@/lib/api").then(({ default: api }) => {
      api.get("/config/nav-visibility").then((r) => setNavVisibility(r.data || {})).catch(() => {});
      api.get("/config/nav-order").then((r) => setNavOrder(r.data || {})).catch(() => {});
    });
  }, [user?.user_id]);

  const navItems = React.useMemo(() => {
    const merged = getNavForRoles(user?.role, user?.secondary_roles, user?.is_intern);
    const rolesHeld = new Set([user?.role, ...(user?.secondary_roles || [])].filter(Boolean));
    if (user?.is_intern) rolesHeld.add("intern");
    // Union of hidden keys across every role the user holds — a nav item is
    // hidden if ANY of the user's roles has that key hidden. Simple and
    // intuitive for admins.
    const hidden = new Set();
    for (const r of rolesHeld) for (const k of (navVisibility?.[r] || [])) hidden.add(k);
    const passes = (it) => !hidden.has(it.to) && !hidden.has(`group:${it.label}`);
    const filtered = hidden.size === 0
      ? merged
      : merged
          .map((it) => it.group ? { ...it, items: (it.items || []).filter(passes) } : it)
          .filter((it) => passes(it) && (!it.group || (it.items || []).length > 0));
    // Apply the FIRST configured ordering that matches one of the roles the
    // user holds. This keeps behaviour predictable for multi-role users.
    let orderList = null;
    for (const r of rolesHeld) {
      if ((navOrder?.[r] || []).length > 0) { orderList = navOrder[r]; break; }
    }
    if (!orderList) return filtered;
    const idxOf = (it) => {
      const key = it.group ? `group:${it.label}` : it.to;
      const i = orderList.indexOf(key);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...filtered].sort((a, b) => idxOf(a) - idxOf(b));
  }, [user?.role, user?.secondary_roles, user?.is_intern, navVisibility, navOrder]);

  // Activity heartbeat — pings backend every minute while the tab is visible,
  // feeding the per-user time-on-platform analytics. Fire-and-forget; failures
  // never disturb the UI.
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      import("@/lib/api").then(({ default: api }) => api.post("/activity/heartbeat").catch(() => {}));
    };
    ping();
    const id = setInterval(ping, 60_000);
    return () => clearInterval(id);
  }, [user]);

  // Auto clock-out after 5 minutes of inactivity (no mouse / keyboard / touch
  // events, or tab hidden). Watches lightweight browser events and fires the
  // same POST /intern/clock endpoint used by the Clock-out button. Only runs
  // for employees / interns — clients don't have attendance rows.
  useEffect(() => {
    if (!user) return;
    const role = user.role;
    if (!(role === "intern" || role === "employee" || role === "mentor" || role === "tutor" || user.is_intern)) return;
    const IDLE_MS = 5 * 60 * 1000;
    const CHECK_MS = 60 * 1000; // run the idle check once per minute only
    let lastActive = Date.now();
    let already = false;
    const clockOut = () => {
      if (already) return;
      already = true;
      import("@/lib/api").then(({ default: api }) =>
        api.post("/intern/clock", { action: "out" }).catch(() => {}));
    };
    const markActive = () => { lastActive = Date.now(); already = false; };
    const events = ["mousemove", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const onVis = () => { if (document.visibilityState === "hidden") clockOut(); else markActive(); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(() => {
      if (Date.now() - lastActive >= IDLE_MS) clockOut();
    }, CHECK_MS);
    return () => {
      clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  // Track which admin group is open. Default: open the group containing active route.
  const initialOpen = React.useMemo(() => {
    if (!isAdmin) return {};
    const open = {};
    for (const g of ADMIN_NAV_GROUPS) {
      if (g.items.some((it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"))) {
        open[g.key] = true;
      }
    }
    // Always pin "overview"
    open.overview = true;
    return open;
  }, [isAdmin, location.pathname]);
  const [openGroups, setOpenGroups] = React.useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("kb.sidebar.admin.openGroups") || "{}");
      return { ...initialOpen, ...saved };
    } catch { return initialOpen; }
  });
  React.useEffect(() => { setOpenGroups((prev) => ({ ...prev, ...initialOpen })); }, [initialOpen]);

  const toggleGroup = (k) => setOpenGroups((p) => {
    const next = { ...p, [k]: !p[k] };
    try { localStorage.setItem("kb.sidebar.admin.openGroups", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  // Collapsed-sidebar state — persisted in localStorage so users can pin their preference.
  const [collapsed, setCollapsed] = React.useState(() => {
    try { return localStorage.getItem("kb.sidebar.collapsed") !== "false"; } catch { return true; }
  });
  React.useEffect(() => { try { localStorage.setItem("kb.sidebar.collapsed", String(collapsed)); } catch { /* ignore */ } }, [collapsed]);

  const onLogout = async () => { await logout?.(); navigate("/", { replace: true }); };

  return (
    <div className="min-h-screen bg-kb-bg flex" data-testid="dashboard-layout">
      <aside className={`hidden lg:flex ${collapsed ? "w-0 -ml-1 overflow-hidden border-r-0" : "w-64"} shrink-0 flex-col border-r border-slate-200 bg-white transition-all duration-200 sticky top-0 h-screen`}>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={`hidden lg:flex fixed top-1/2 -translate-y-1/2 z-40 ${collapsed ? "left-2" : "left-[15.25rem]"} w-6 h-14 items-center justify-center rounded-r-full rounded-l-none border border-slate-200 border-l-0 bg-white text-kb-ink-muted hover:text-kb-primary hover:border-kb-primary shadow-md transition-all`}
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? "Show menu (Ctrl+B)" : "Hide menu (Ctrl+B)"}
          aria-label={collapsed ? "Show menu" : "Hide menu"}
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
        <Link to="/" className="px-6 h-16 flex items-center gap-2.5 border-b border-slate-200">
          <img
            src={BRAND_ASSETS.logoShapePurple}
            alt="KhojByte"
            className="w-8 h-8 object-contain select-none"
            draggable="false"
            data-testid="sidebar-logo-img"
          />
          <div className="leading-tight">
            <div className="font-serif text-base text-kb-ink">KhojByte</div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted -mt-0.5">
              Research Lab
            </div>
          </div>
        </Link>

        <div className="px-6 py-4 border-b border-slate-200">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SIGNED IN AS</div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <Building2 className="w-3.5 h-3.5 text-kb-primary shrink-0" strokeWidth={1.5} />
            <span className="text-xs font-medium text-kb-ink">{ROLE_LABELS[user?.role] || user?.role || "—"}</span>
            {/* Multi-role chips: every additional capability the user holds. */}
            {(user?.secondary_roles || []).filter((r) => r && r !== user.role).map((r) => (
              <span
                key={r}
                className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-kb-subtle border border-kb-primary/20 text-kb-primary"
                title={`Additional role: ${ROLE_LABELS[r] || r}`}
                data-testid={`sidebar-role-chip-${r}`}
              >
                +{ROLE_LABELS[r] || r}
              </span>
            ))}
            {user?.is_intern && user.role !== "intern" && !(user?.secondary_roles || []).includes("intern") && (
              <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-700" title="Also flagged as intern">
                +Intern
              </span>
            )}
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-auto">
          {isAdmin ? (
            ADMIN_NAV_GROUPS.map((g) => {
              const isOpen = !!openGroups[g.key];
              if (!g.label) {
                // ungrouped pinned items (Overview)
                return g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end}
                    data-testid={`sidebar-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-link`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                        isActive ? "bg-kb-subtle text-kb-primary font-medium" : "text-kb-ink-muted hover:text-kb-primary hover:bg-slate-50"
                      }`
                    }
                  >
                    <it.icon className="w-4 h-4" strokeWidth={1.6} />
                    <span>{it.label}</span>
                  </NavLink>
                ));
              }
              const GroupIcon = g.icon;
              return (
                <div key={g.key} className="pt-1">
                  <button
                    onClick={() => toggleGroup(g.key)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-sm text-[11px] uppercase tracking-[0.14em] font-semibold text-kb-ink-muted hover:bg-slate-50"
                    data-testid={`sidebar-group-${g.key}`}
                  >
                    <span className="flex items-center gap-2">
                      {GroupIcon && <GroupIcon className="w-3.5 h-3.5" strokeWidth={1.6} />}
                      {g.label}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} strokeWidth={1.6} />
                  </button>
                  {isOpen && (
                    <div className="ml-1 mt-0.5 space-y-0.5">
                      {g.items.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          data-testid={`sidebar-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-link`}
                          className={({ isActive }) =>
                            `flex items-center gap-3 pl-7 pr-3 py-2 rounded-sm text-sm transition-colors ${
                              isActive ? "bg-kb-subtle text-kb-primary font-medium" : "text-kb-ink-muted hover:text-kb-primary hover:bg-slate-50"
                            }`
                          }
                        >
                          <it.icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                          <span>{it.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            navItems.map((item) => {
              if (item.group) {
                return (
                  <details key={item.label} className="rounded-sm" data-testid={`sidebar-group-${item.label.toLowerCase()}`}>
                    <summary className="flex items-center gap-3 px-3 py-2.5 text-sm text-kb-ink-muted hover:text-kb-primary hover:bg-slate-50 cursor-pointer rounded-sm list-none">
                      <item.icon className="w-4 h-4" strokeWidth={1.6} />
                      <span className="flex-1">{item.label}</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </summary>
                    <div className="ml-7 mt-1 space-y-0.5">
                      {item.items.map(({ to, label, icon: SubIcon }) => (
                        <NavLink key={to} to={to}
                          data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-link`}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs transition-colors ${
                              isActive ? "bg-kb-subtle text-kb-primary font-medium" : "text-kb-ink-muted hover:text-kb-primary hover:bg-slate-50"
                            }`
                          }
                        >
                          {SubIcon ? <SubIcon className="w-3.5 h-3.5" strokeWidth={1.6} /> : null}
                          <span>{label}</span>
                        </NavLink>
                      ))}
                    </div>
                  </details>
                );
              }
              const { to, label, icon: Icon, end, activePrefixes } = item;
              const forceActive = Array.isArray(activePrefixes) && activePrefixes.some((p) => location.pathname.startsWith(p));
              const showAnnouncementBadge = to === "/dashboard/announcements";
              const showMeetingsBadge = to === "/dashboard/employee/meetings";
              const showChatBadge = to === "/dashboard/chat";
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`sidebar-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-link`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                      (isActive || forceActive) ? "bg-kb-subtle text-kb-primary font-medium" : "text-kb-ink-muted hover:text-kb-primary hover:bg-slate-50"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                  <span>{label}</span>
                  {showAnnouncementBadge && <AnnouncementsNavBadge />}
                  {showMeetingsBadge && <MeetingsNavBadge />}
                  {showChatBadge && <ChatNavBadge />}
                </NavLink>
              );
            })
          )}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <Link to="/" className="kb-mono text-xs text-kb-ink-muted flex items-center gap-2 hover:text-kb-primary mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO SITE
          </Link>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover shrink-0" data-testid="sidebar-avatar" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-kb-subtle border border-kb-primary/20 text-kb-primary flex items-center justify-center text-[11px] font-bold shrink-0" data-testid="sidebar-avatar-fallback">
                  {(user?.name || user?.email || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-kb-ink truncate">{user?.name}</div>
                <div className="text-xs text-kb-ink-muted truncate">{user?.email}</div>
                <Link to="/account/specialization" className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary hover:underline" data-testid="sidebar-specialization-link">
                  My specialization
                </Link>
              </div>
            </div>
            <button onClick={onLogout} className="p-2 text-kb-ink-muted hover:text-kb-primary rounded-sm" data-testid="sidebar-logout-btn" title="Log out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <AnnouncementsBellRow />
          <ClockToggle user={user} />
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <UpcomingEventsBanner />
        <VerifyEmailBanner user={user} />
        <SpecializationNudge user={user} />
        <div className="lg:hidden bg-white border-b border-slate-200 h-14 px-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src={BRAND_ASSETS.logoShapePurple}
              alt="KhojByte"
              className="w-7 h-7 object-contain select-none"
              draggable="false"
              data-testid="mobile-dashboard-logo"
            />
            <span className="font-serif text-base text-kb-ink">Dashboard</span>
          </Link>
          <button onClick={onLogout} className="text-sm text-kb-ink-muted" data-testid="mobile-logout-btn">Log out</button>
        </div>

        <div
          className="lg:hidden order-2 sticky bottom-0 bg-white border-t border-slate-200 z-30 overflow-x-auto kb-mobile-bottom-nav"
          data-testid="mobile-bottom-nav"
        >
          <div className="flex min-w-max">
            {navItems.flatMap((item) => item.group ? item.items : [item]).map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `flex flex-col items-center justify-center px-4 py-2.5 text-[10px] shrink-0 min-w-[72px] ${isActive ? "text-kb-purple" : "text-kb-ink-muted"}`}
                data-testid={`mobile-nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                <span className="mt-1 whitespace-nowrap">{label.split(" ")[0]}</span>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="flex-1 px-3 lg:px-5 pt-2 lg:pt-3 pb-6 lg:pb-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

// ----- Verify-email banner: shown only for password users who haven't confirmed their inbox. -----
function VerifyEmailBanner({ user }) {
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  if (!user || user.email_verified || user.auth_provider !== "email") return null;
  const resend = async () => {
    setBusy(true);
    try {
      const api = (await import("@/lib/api")).default;
      const { toast } = await import("sonner");
      await api.post("/auth/send-verification");
      toast.success("Verification email re-sent — check your inbox.");
      setSent(true);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("Couldn't re-send verification email.");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 lg:px-10 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="verify-email-banner">
      <div className="text-amber-900">
        <strong>Verify your email</strong> — we sent a confirmation link to <span className="kb-mono">{user.email}</span>. Some features stay locked until you confirm.
      </div>
      <button onClick={resend} disabled={busy || sent}
        className="kb-mono text-[10px] uppercase tracking-wider text-amber-900 border border-amber-300 hover:bg-amber-100 px-3 py-1.5 rounded-sm disabled:opacity-50"
        data-testid="verify-email-resend-btn">
        {busy ? "Sending…" : sent ? "Sent — check inbox" : "Re-send link"}
      </button>
    </div>
  );
}

/**
 * Soft dashboard banner nudging employees/interns to pick a specialization
 * so admins' domain-based task assignment covers them. Fetches the fresh
 * user record from /auth/me (auth context is snapshot-y). Dismissible for
 * the session via localStorage.
 */
function SpecializationNudge({ user }) {
  const [fresh, setFresh] = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    setDismissed(localStorage.getItem("kb.specialization_nudge.dismissed") === "1");
    if (!user) return;
    if (!["employee", "intern"].includes(user.role) && !user.is_intern) return;
    (async () => {
      try {
        const api = (await import("@/lib/api")).default;
        const r = await api.get("/auth/me");
        setFresh(r.data);
      } catch { /* no-op */ }
    })();
  }, [user?.user_id]);
  if (!fresh || dismissed) return null;
  if (!["employee", "intern"].includes(fresh.role) && !fresh.is_intern) return null;
  if (fresh.specialization_taxonomy_id) return null;
  const dismiss = () => {
    localStorage.setItem("kb.specialization_nudge.dismissed", "1");
    setDismissed(true);
  };
  return (
    <div className="bg-indigo-50 border-b border-indigo-200 px-4 lg:px-10 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="specialization-nudge">
      <div className="text-indigo-900">
        <strong>Pick your specialization</strong> — set your research focus so managers can auto-assign you tasks in your domain.
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/account/specialization"
          className="kb-mono text-[10px] uppercase tracking-wider text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-sm"
          data-testid="specialization-nudge-cta"
        >
          Set specialization
        </Link>
        <button onClick={dismiss} className="kb-mono text-[10px] uppercase tracking-wider text-indigo-900 hover:underline" data-testid="specialization-nudge-dismiss">
          Not now
        </button>
      </div>
    </div>
  );
}


function AnnouncementsNavBadge() {
  const [count, setCount] = React.useState(0);
  const fetchCount = React.useCallback(async () => {
    try {
      const r = await (await import("@/lib/api")).default.get("/announcements/unseen-count");
      setCount(r.data?.count || 0);
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => {
    fetchCount();
    const onRefresh = () => fetchCount();
    window.addEventListener("announcements:refresh", onRefresh);
    const t = setInterval(fetchCount, 60000);
    return () => { window.removeEventListener("announcements:refresh", onRefresh); clearInterval(t); };
  }, [fetchCount]);
  if (count <= 0) return null;
  return (
    <span
      className="ml-auto kb-mono text-[10px] uppercase tracking-wider bg-rose-500 text-white px-1.5 py-0.5 rounded-sm font-bold shadow-sm animate-pulse"
      data-testid="nav-announcements-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function MeetingsNavBadge() {
  const [count, setCount] = React.useState(0);
  const fetchCount = React.useCallback(async () => {
    try {
      const r = await (await import("@/lib/api")).default.get("/sidebar/counts");
      setCount(r.data?.meetings || 0);
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => {
    fetchCount();
    const onRefresh = () => fetchCount();
    window.addEventListener("meetings:refresh", onRefresh);
    const t = setInterval(fetchCount, 60000);
    return () => { window.removeEventListener("meetings:refresh", onRefresh); clearInterval(t); };
  }, [fetchCount]);
  if (count <= 0) return null;
  return (
    <span
      className="ml-auto kb-mono text-[10px] uppercase tracking-wider bg-kb-primary text-white px-1.5 py-0.5 rounded-sm font-bold shadow-sm"
      data-testid="nav-meetings-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function ChatNavBadge() {
  const [count, setCount] = React.useState(0);
  const fetchCount = React.useCallback(async () => {
    try {
      const r = await (await import("@/lib/api")).default.get("/sidebar/counts");
      setCount(r.data?.chat || 0);
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => {
    fetchCount();
    // The Chat page fires this event on visit + after every read so the
    // badge clears immediately without waiting for the polling interval.
    const onRefresh = () => fetchCount();
    window.addEventListener("chat:refresh", onRefresh);
    window.addEventListener("dm:refresh", onRefresh);
    const t = setInterval(fetchCount, 45000);
    return () => {
      window.removeEventListener("chat:refresh", onRefresh);
      window.removeEventListener("dm:refresh", onRefresh);
      clearInterval(t);
    };
  }, [fetchCount]);
  if (count <= 0) return null;
  return (
    <span
      className="ml-auto kb-mono text-[10px] uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded-sm font-bold shadow-sm"
      data-testid="nav-chat-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function AnnouncementsBellRow() {
  const [counts, setCounts] = React.useState({ tasks: 0, meetings: 0, announcements: 0 });
  const fetchCounts = React.useCallback(async () => {
    try {
      const r = await (await import("@/lib/api")).default.get("/sidebar/counts");
      setCounts(r.data || { tasks: 0, meetings: 0, announcements: 0 });
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => {
    fetchCounts();
    const onRefresh = () => fetchCounts();
    window.addEventListener("announcements:refresh", onRefresh);
    window.addEventListener("tasks:refresh", onRefresh);
    window.addEventListener("meetings:refresh", onRefresh);
    const t = setInterval(fetchCounts, 60000);
    return () => {
      window.removeEventListener("announcements:refresh", onRefresh);
      window.removeEventListener("tasks:refresh", onRefresh);
      window.removeEventListener("meetings:refresh", onRefresh);
      clearInterval(t);
    };
  }, [fetchCounts]);
  const rows = [
    { to: "/dashboard/employee/kanban", label: "Open tasks",    icon: Briefcase, count: counts.tasks,        testid: "sidebar-notif-tasks" },
    { to: "/dashboard/employee/meetings", label: "Upcoming meets", icon: Calendar, count: counts.meetings,   testid: "sidebar-notif-meetings" },
  ];
  return (
    <div className="mt-3 space-y-1.5" data-testid="sidebar-notifications">
      {rows.map((r) => (
        <Link
          key={r.to}
          to={r.to}
          className="flex items-center gap-2 text-xs text-kb-ink-muted hover:text-kb-primary"
          data-testid={r.testid}
        >
          <r.icon className="w-4 h-4" />
          <span>{r.label}</span>
          {r.count > 0 && (
            <span className="ml-auto kb-mono text-[9px] uppercase tracking-wider bg-kb-primary text-white px-1.5 py-0.5 rounded-sm">
              {r.count > 99 ? "99+" : r.count}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
}



// Compact clock-in / clock-out widget pinned under the account row so staff
// (interns / employees / mentors / tutors) can toggle attendance from any
// page — including the task workspace. Reuses POST /intern/clock.
function ClockToggle({ user }) {
  const role = user?.role;
  const isStaff = role === "intern" || role === "employee" || role === "mentor" || role === "tutor" || user?.is_intern;
  const [clockedIn, setClockedIn] = React.useState(null); // null → loading, true/false → known
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!isStaff) return;
    import("@/lib/api").then(({ default: api }) =>
      api.get("/employee/attendance?days=1").then((r) => {
        const today = (r.data || [])[0];
        const sessions = today?.sessions || [];
        setClockedIn(sessions.length > 0 && !sessions[sessions.length - 1]?.out);
      }).catch(() => setClockedIn(false))
    );
  }, [isStaff]);
  if (!isStaff) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const action = clockedIn ? "out" : "in";
      await (await import("@/lib/api")).default.post("/intern/clock", { action });
      setClockedIn(!clockedIn);
    } catch (e) {
      try { const { toast } = await import("sonner"); toast.error(e?.response?.data?.detail || "Clock failed"); } catch { /* ignore */ }
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || clockedIn === null}
      className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-sm border transition-colors ${
        clockedIn
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : "border-slate-200 bg-white text-kb-ink hover:border-kb-primary hover:text-kb-primary"
      }`}
      data-testid="sidebar-clock-toggle"
      title={clockedIn ? "Clock out" : "Clock in"}
    >
      <Clock className="w-3.5 h-3.5" />
      {clockedIn === null ? "…" : clockedIn ? "Clocked in · Clock out" : "Clock in"}
    </button>
  );
}
